import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useConsult } from "@/App";

const EMPTY = { name: "", email: "", phone: "", business: "", message: "", preferred: "" };

export default function ConsultationModal() {
  const { consultOpen, closeConsult } = useConsult();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/submissions", {
        type: "consultation",
        name: form.name,
        email: form.email,
        phone: form.phone,
        business: form.business,
        message: form.message,
        meta: { preferred: form.preferred },
      });
      setDone(true);
      if (window.gtag) window.gtag("event", "book_consultation", { location: "modal" });
      toast.success("Consultation request received. We reply within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    closeConsult();
    setTimeout(() => { setDone(false); setForm(EMPTY); }, 300);
  };

  return (
    <Dialog open={consultOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="border-gold/25 bg-navy-surface text-ivory sm:max-w-lg" data-testid="consultation-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ivory">Book a free consultation</DialogTitle>
          <DialogDescription className="text-slate-400">
            Thirty minutes, plain English, no obligation. We will map your quickest digital wins.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="consultation-success">
            <CheckCircle2 className="h-14 w-14 text-gold" />
            <p className="font-serif text-xl">Thank you — request received.</p>
            <p className="max-w-sm text-sm text-slate-400">A specialist from our Antigua office will confirm your session within one business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-2 space-y-4" data-testid="consultation-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={form.name} onChange={set("name")} placeholder="Full name" className="input-lux" data-testid="consultation-name-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="input-lux" data-testid="consultation-email-input" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className="input-lux" data-testid="consultation-phone-input" />
              <input value={form.business} onChange={set("business")} placeholder="Business name" className="input-lux" data-testid="consultation-business-input" />
            </div>
            <input value={form.preferred} onChange={set("preferred")} placeholder="Preferred day or time (optional)" className="input-lux" data-testid="consultation-preferred-input" />
            <textarea value={form.message} onChange={set("message")} rows={3} placeholder="What would you like to achieve?" className="input-lux resize-none" data-testid="consultation-message-input" />
            <button
              type="submit"
              disabled={loading}
              data-testid="consultation-submit-btn"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request my consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
